import type {
    AdIntelligenceRecordsSummaryRecord,
} from "@similarweb/pro-api-client";

import { useApiQuery } from "app/modules/api-client";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";

import type { AdIntelligenceController } from "../../shared/types/types";

const createRow = (
    activity: string,
    domains: string[],
    getData: (domain: string) => AdIntelligenceRecordsSummaryRecord,
) => {
    return domains.reduce(
        (acc, domain) => {
            acc[domain] = getData(domain);
            return acc;
        },
        {
            activity,
        } as {
            activity: string;
            [domain: string]: string | AdIntelligenceRecordsSummaryRecord;
        },
    );
};

export type ActivityBreakdownData = {
    isFromNewScraper: boolean;
    startDate?: string;
    domains: string[];
    favicons: Record<string, string>;
    data: {
        activity: string;
        [domain: string]: string | AdIntelligenceRecordsSummaryRecord;
    }[];
};

export const useActivityBreakdownCompare = (controller: AdIntelligenceController) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { from, to, isWindow } = DurationService.getDurationData(params.duration).forAPI;
    const keyArray = params.key.split(",");

    const entitiesMethodName =
        controller === "advertiser" ? "adIntelligenceAdvertiserSummaryList" : "adIntelligencePublisherSummaryList";

    const breakdownMethodName =
        controller === "advertiser"
            ? "adIntelligenceAdvertiserPublishersSummaryList"
            : "adIntelligencePublisherAdvertisersSummaryList";

    const entitiesQuery = useApiQuery<"AdIntelligenceMediaPartners", typeof entitiesMethodName, ActivityBreakdownData>(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName: entitiesMethodName,
            options: {
                select: (response) => {
                    const domains = keyArray.filter((domain) => domain in response.data);
                    const favicons = domains.reduce(
                        (acc, domain) => {
                            acc[domain] = response.data[domain]?.favicon || "";
                            return acc;
                        },
                        {} as Record<string, string>,
                    );

                    return {
                        isFromNewScraper: response.isFromNewScraper,
                        startDate: response.startDate ?? undefined,
                        domains,
                        favicons,
                        data: [
                            createRow(
                                "campaigns",
                                domains,
                                (domain) => response.data[domain]?.campaigns ?? { count: 0 },
                            ),
                            createRow(
                                "creatives",
                                domains,
                                (domain) => response.data[domain]?.creatives ?? { count: 0 },
                            ),
                        ],
                    };
                },
            },
        },
        {
            country: params.country,
            key: keyArray,
            from,
            to,
            isWindow,
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        },
    );

    const thirdEntityQuery = useApiQuery<
        "AdIntelligenceMediaPartners",
        typeof breakdownMethodName,
        Record<string, AdIntelligenceRecordsSummaryRecord | undefined>
    >(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName: breakdownMethodName,
            options: {
                select: (response) => {
                    if (!response?.data) {
                        return {};
                    }
                    const result: Record<string, AdIntelligenceRecordsSummaryRecord | undefined> = {};
                    for (const domain of keyArray) {
                        const siteData = response.data[domain];
                        if (siteData) {
                            result[domain] =
                                controller === "advertiser"
                                    ? (siteData as { publishers?: AdIntelligenceRecordsSummaryRecord }).publishers
                                    : (siteData as { advertisers?: AdIntelligenceRecordsSummaryRecord }).advertisers;
                        }
                    }
                    return result;
                },
            },
        },
        {
            country: params.country,
            key: keyArray,
            from,
            to,
            isWindow,
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        },
    );

    const thirdRowEntity = controller === "advertiser" ? "publishers" : "advertisers";
    const entitiesData = entitiesQuery.data;
    let combinedEntitiesData: ActivityBreakdownData | undefined = entitiesData;

    if (entitiesData && thirdEntityQuery.data) {
        const { domains } = entitiesData;
        const thirdRow = createRow(thirdRowEntity, domains, (domain) => thirdEntityQuery.data[domain] ?? { count: 0 });

        combinedEntitiesData = {
            ...entitiesData,
            data: [...entitiesData.data, thirdRow],
        };
    }

    return {
        data: combinedEntitiesData,
        loading: entitiesQuery.isPending || thirdEntityQuery.isPending,
        error: entitiesQuery.isError || thirdEntityQuery.isError,
    };
};
